import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const JoinLoyaltyPrompt = ({ email, name }) => {
  const [member, setMember] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    if (!email) {
      setMember(true);
      return;
    }
    api
      .get("/loyalty/status", { params: { email } })
      .then(({ data }) => setMember(!!data.member))
      .catch(() => setMember(true));
  }, [email]);

  const join = async () => {
    setJoining(true);
    try {
      const { data } = await api.post("/loyalty/signup", { name: name || "Island Explorer", email });
      setJoined(data);
      toast.success(data.needs_verification ? "Check your inbox to activate your membership!" : "Welcome to Easy Drive Loyalty!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join right now — try again from the Loyalty page");
    } finally {
      setJoining(false);
    }
  };

  if (!email || member !== false) return null;

  if (joined) {
    return (
      <div data-testid="join-loyalty-success" className="mt-8 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={22} className="text-emerald-600" />
          <p className="font-ui font-semibold">
            {joined.needs_verification ? "One quick step — verify your email" : "You're in — welcome to Easy Drive Loyalty!"}
          </p>
        </div>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          {joined.needs_verification
            ? `We sent a verification link to ${email}. Tap it to activate your membership — then every completed rental moves you up a tier.`
            : `Your rental counter starts now. Every completed rental with ${email} moves you up a tier and your discount applies automatically at checkout. We've emailed you the full tier levels and rules.`}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="join-loyalty-prompt" className="mt-8 rounded-2xl border border-primary/30 bg-card p-6">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="gold-text" />
        <p className="font-ui text-[11px] tracking-[0.25em] uppercase text-muted-foreground">One click · free forever</p>
      </div>
      <p className="mt-3 font-display text-2xl tracking-tight">Join Easy Drive Loyalty</p>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
        Start earning from your next rental — tiers unlock up to 15% off, half-price extras, and free
        GPS &amp; baby seat at the top tier. We'll sign you up with{" "}
        <span className="text-foreground font-medium">{email}</span>.
      </p>
      <Button
        data-testid="join-loyalty-button"
        onClick={join}
        disabled={joining}
        className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui"
      >
        {joining && <Loader2 size={15} className="animate-spin mr-2" />}
        {joining ? "Joining…" : "Join now — it's free"}
      </Button>
    </div>
  );
};
